import { toast } from "@/components/ui/sonner";

// Our own portal's pages that embed this widget and know how to render a
// forwarded error (merchant repo: Index.jsx's live demo, GetStartedPage.jsx)
// — a genuine third-party partner's site has no such listener, so the
// widget must fall back to showing the toast locally there.
const TRUSTED_PARENT_HOSTS = ["p2p.palzea.com", "p2p-stage.palzea.com", "207.154.227.91"];

function isEmbedded() {
    try {
        return window.self !== window.top;
    } catch {
        // Cross-origin parent throws on access to window.top in some
        // browsers - that access itself is only reachable when embedded.
        return true;
    }
}

function getParentHost() {
    try {
        if (!document.referrer) return null;
        return new URL(document.referrer).hostname;
    } catch {
        return null;
    }
}

// document.referrer is what identifies the parent here - a cross-origin
// iframe can never read window.parent.location directly. The merchant
// repo's iframes use referrerPolicy="no-referrer-when-downgrade" precisely
// so this is populated for same-scheme (https->https) embeds; if a
// stricter referrer policy or private-browsing mode blocks it, this simply
// (and safely) falls back to showing the toast locally instead of
// forwarding it nowhere.
export function isTrustedParentOrigin() {
    if (!isEmbedded()) return false;
    const host = getParentHost();
    return !!host && TRUSTED_PARENT_HOSTS.includes(host);
}

// Central error-toast entry point, called alongside (not instead of) each
// call site's existing inline error display. Behavior depends on how this
// widget instance is running:
//   - Standalone, not in an iframe at all: does nothing - errors keep
//     showing exactly as they always have (the inline banner/field error),
//     with no added toast.
//   - Embedded on one of our own portal's pages (a trusted origin): forwarded
//     via postMessage so the PARENT page shows the toast instead of it
//     appearing inside the cramped widget iframe (see the
//     WIDGET_ERROR_TOAST listeners in the merchant repo).
//   - Embedded anywhere else (a genuine third-party partner's site): shown
//     as a toast inside the widget itself, since there's no parent listener
//     to forward to.
export function showErrorToast(message, options) {
    if (!isEmbedded()) return;
    showErrorAsToast(message, options);
}

// For the rare call site where a toast (sonner or the shadcn use-toast
// hook) is already the sole, pre-existing error mechanism - not one being
// newly added alongside an inline banner. Unlike showErrorToast, this never
// no-ops: standalone keeps showing its own toast exactly as it always has,
// embedded-on-a-trusted-origin forwards to the parent INSTEAD of showing it
// locally too (avoiding a redundant double toast), and embedded elsewhere
// shows it locally, same as standalone.
export function showErrorAsToast(message, options) {
    if (isTrustedParentOrigin()) {
        try {
            window.parent.postMessage({ type: "WIDGET_ERROR_TOAST", message }, "*");
            return;
        } catch {
            // fall through to local toast
        }
    }
    toast.error(message, options);
}
